import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AppProvider } from './context/AppContext';
import { BrandProvider } from './context/BrandContext';
import { CopyProvider } from './context/CopyContext';
import { useConfig } from './context/ConfigContext';
import { setApiBaseUrl } from './services/apiService';
import { getWebSocketService } from './services/websocketService';
import TestModeBar from './components/TestModeBar';
import ADAGuidelines from './components/ADAGuidelines';
import WelcomeView from './components/views/WelcomeView';
import BeforeStartView from './components/views/BeforeStartView';
import AddEmptyView from './components/views/AddEmptyView';
import CheckingEmptyView from './components/views/CheckingEmptyView';
import EmptyAcceptedView from './components/views/EmptyAcceptedView';
import PlaceEmptyView from './components/views/PlaceEmptyView';
import RemoveEmptyView from './components/views/RemoveEmptyView';
import EmailCaptureView from './components/views/EmailCaptureView';
import MembershipSelectionView from './components/views/MembershipSelectionView';
import RewardMethodSelectionView from './components/views/RewardMethodSelectionView';
import SummaryView from './components/views/SummaryView';
import LegalView from './components/views/LegalView';
import OfferView from './components/views/OfferView';
import ConfirmationView from './components/views/ConfirmationView';
import MemberWelcomeView from './components/views/MemberWelcomeView';
import SignInView from './components/views/SignInView';
import SignUpView from './components/views/SignUpView';
import KioskSignInView from './components/views/KioskSignInView';
import KioskSignUpView from './components/views/KioskSignUpView';
import ProductRatingView from './components/views/ProductRatingView';
import ProductAspectSelectionView from './components/views/ProductAspectSelectionView';
import PurchaseReasonSelectionView from './components/views/PurchaseReasonSelectionView';
import RateReturnsView from './components/views/RateReturnsView';
import ConfigParamsPersist from './components/ConfigParamsPersist';



function AppContent() {
  const location = useLocation();

  return (
    <div className="relative min-h-screen">
      {/* Global Test Mode Bar */}
      <TestModeBar />
      
      {/* Global ADA Guidelines */}
      <ADAGuidelines />
      
      {/* Main Content */}
      <div className="min-h-screen">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<WelcomeView />} />
            <Route path="/before-start" element={<BeforeStartView />} />
            <Route path="/add-empty" element={<AddEmptyView />} />
            <Route path="/checking-empty" element={<CheckingEmptyView />} />
            <Route path="/empty-accepted" element={<EmptyAcceptedView />} />
            <Route path="/place-empty" element={<PlaceEmptyView />} />
            <Route path="/remove-empty" element={<RemoveEmptyView />} />
            <Route path="/email-capture" element={<EmailCaptureView />} />
            <Route path="/membership-selection" element={<MembershipSelectionView />} />
            <Route path="/reward-method-selection" element={<RewardMethodSelectionView />} />
            <Route path="/summary" element={<SummaryView />} />
            <Route path="/legal" element={<LegalView />} />
            <Route path="/rate-returns" element={<RateReturnsView />} />
            <Route path="/product-rating" element={<ProductRatingView />} />
            <Route path="/product-aspect-selection" element={<ProductAspectSelectionView />} />
            <Route path="/purchase-reason-selection" element={<PurchaseReasonSelectionView />} />
            <Route path="/offer" element={<OfferView />} />
            <Route path="/confirmation" element={<ConfirmationView />} />
            <Route path="/member-welcome" element={<MemberWelcomeView />} />
            <Route path="/sign-in" element={<SignInView />} />
            <Route path="/sign-up" element={<SignUpView />} />
            <Route path="/kiosk-sign-in" element={<KioskSignInView />} />
          <Route path="/kiosk-sign-up" element={<KioskSignUpView />} />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}

function App() {
  const { config } = useConfig();

  // Initialize services with runtime configuration
  useEffect(() => {
    // Config is null if URL params are missing - ConfigLoader handles that
    if (!config) return;

    // Set API base URL
    setApiBaseUrl(config.apiBaseUrl);

    // Initialize WebSocket service with configured URL
    // This creates the singleton if it doesn't exist
    getWebSocketService(config.wsBaseUrl);

    console.log('Services initialized with config:', {
      apiBaseUrl: config.apiBaseUrl,
      wsBaseUrl: config.wsBaseUrl,
      kioskId: config.kioskId
    });
  }, [config]);

  return (
    <BrandProvider>
      <AppProvider>
        <CopyProvider>
          <Router>
            <ConfigParamsPersist />
            <AppContent />
          </Router>
        </CopyProvider>
      </AppProvider>
    </BrandProvider>
  );
}

export default App;
